import { useMemo } from 'react';
import EditableCell from './EditableCell';
import { formatCurrency, formatPercent } from '../../utils/formatting';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_HEADERS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const INCOME_ITEMS = [
  { key: 'commissions', label: 'Commissions' },
  { key: 'investment_management', label: 'Investment Management' },
  { key: 'subscription_fees', label: 'Subscription Fees' },
  { key: 'tax_preparation', label: 'Tax Preparation' },
  { key: 'bookkeeping', label: 'Bookkeeping' },
  { key: 'cao', label: 'Fractional CAO' },
  { key: 'tax_planning', label: 'Tax Plan' },
];

const COGS_ITEMS = [
  { key: 'payroll_taxes_direct', label: 'Payroll Taxes (Direct)' },
  { key: 'payroll_wages_direct', label: 'Payroll Wages (Direct)' },
  { key: 'referral_fees', label: 'Referral Fees' },
];

const EXPENSE_ITEMS = [
  { key: 'advertising_promotional', label: 'Advertising/Promotional' },
  { key: 'bank_service_charges', label: 'Bank Service Charges' },
  { key: 'business_licenses_and_permits', label: 'Business Licenses and Permits' },
  { key: 'client_service_fees', label: 'Client Service Fees' },
  { key: 'consultant', label: 'Consultant' },
  { key: 'continuing_education', label: 'Continuing Education' },
  { key: 'dues_and_subscriptions', label: 'Dues and Subscriptions' },
  { key: 'gifts', label: 'Gifts' },
  { key: 'insurance_expense', label: 'Insurance Expense' },
  { key: 'mergers_and_acquisitions', label: 'M&A' },
  { key: 'meals_and_entertainment', label: 'Meals and Entertainment' },
  { key: 'miscellaneous_expenses', label: 'Miscellaneous Expenses' },
  { key: 'office_supplies', label: 'Office Supplies' },
  { key: 'payroll_non_employee', label: 'Payroll (Non-Employee Comp.)' },
  { key: 'postage_and_delivery', label: 'Postage and Delivery' },
  { key: 'professional_fees', label: 'Professional Fees' },
  { key: 'software_subscriptions', label: 'Software Subscriptions' },
  { key: 'telephone_expense', label: 'Telephone Expense' },
  { key: 'travel_expense', label: 'Travel Expense' },
  { key: 'utilities', label: 'Utilities' },
];

function sumCategory(monthData, category) {
  if (!monthData || !monthData[category]) return 0;
  return Object.values(monthData[category]).reduce((sum, v) => sum + (v || 0), 0);
}

function formatYoY(current, prior) {
  if (prior == null || prior === 0) return '--';
  const growth = (current - prior) / Math.abs(prior);
  const color = growth >= 0 ? 'text-deep-teal' : 'text-rose';
  return { text: `${growth >= 0 ? '+' : ''}${(growth * 100).toFixed(1)}%`, color };
}

export default function PLTable({ data, editable, onCellChange, actualsCutoff, priorYearData, forecastRevenue, scenarioImpact }) {
  const { months } = data;
  const hasPriorYear = priorYearData && priorYearData.months;
  const hasForecast = forecastRevenue && forecastRevenue.some(v => v > 0);
  const hasScenarios = scenarioImpact && scenarioImpact.some(m => m.net !== 0);

  const monthlyTotals = useMemo(() => {
    return MONTHS.map((m, i) => {
      const md = months[m];
      const forecastAdd = hasForecast ? (forecastRevenue[i] || 0) : 0;
      const scenarioRevAdd = hasScenarios ? Math.max(scenarioImpact[i]?.revenue || 0, 0) : 0;
      const scenarioCostAdd = hasScenarios ? Math.min(scenarioImpact[i]?.cost || 0, 0) : 0;
      const totalIncome = sumCategory(md, 'income') + forecastAdd + scenarioRevAdd;
      const totalCogs = sumCategory(md, 'cogs');
      const grossProfit = totalIncome - totalCogs;
      // Scenario costs (negative values like salaries, expenses) add to expenses
      const totalExpenses = sumCategory(md, 'expenses') + Math.abs(scenarioCostAdd);
      const netOperatingIncome = grossProfit - totalExpenses;
      return { totalIncome, totalCogs, grossProfit, totalExpenses, netOperatingIncome };
    });
  }, [months, hasForecast, forecastRevenue, hasScenarios, scenarioImpact]);

  const ytd = useMemo(() => {
    return monthlyTotals.reduce(
      (acc, mt) => ({
        totalIncome: acc.totalIncome + mt.totalIncome,
        totalCogs: acc.totalCogs + mt.totalCogs,
        grossProfit: acc.grossProfit + mt.grossProfit,
        totalExpenses: acc.totalExpenses + mt.totalExpenses,
        netOperatingIncome: acc.netOperatingIncome + mt.netOperatingIncome,
      }),
      { totalIncome: 0, totalCogs: 0, grossProfit: 0, totalExpenses: 0, netOperatingIncome: 0 }
    );
  }, [monthlyTotals]);

  const priorYtd = useMemo(() => {
    if (!hasPriorYear) return null;
    const pm = priorYearData.months;
    return MONTHS.reduce(
      (acc, m) => {
        const totalIncome = sumCategory(pm[m], 'income');
        const totalCogs = sumCategory(pm[m], 'cogs');
        const grossProfit = totalIncome - totalCogs;
        const totalExpenses = sumCategory(pm[m], 'expenses');
        const netOperatingIncome = grossProfit - totalExpenses;
        return {
          totalIncome: acc.totalIncome + totalIncome,
          totalCogs: acc.totalCogs + totalCogs,
          grossProfit: acc.grossProfit + grossProfit,
          totalExpenses: acc.totalExpenses + totalExpenses,
          netOperatingIncome: acc.netOperatingIncome + netOperatingIncome,
        };
      },
      { totalIncome: 0, totalCogs: 0, grossProfit: 0, totalExpenses: 0, netOperatingIncome: 0 }
    );
  }, [hasPriorYear, priorYearData]);

  function lineItemYTD(category, key) {
    return MONTHS.reduce((sum, m) => sum + (months[m]?.[category]?.[key] || 0), 0);
  }

  function priorLineItemYTD(category, key) {
    if (!hasPriorYear) return null;
    return MONTHS.reduce((sum, m) => sum + (priorYearData.months[m]?.[category]?.[key] || 0), 0);
  }

  function pctOfIncome(value) {
    if (!ytd.totalIncome) return 0;
    return value / ytd.totalIncome;
  }

  // Determine if a column is estimated
  function isEstimateCol(monthIdx) {
    return actualsCutoff != null && monthIdx >= actualsCutoff;
  }

  // ---- Render helpers ----

  function renderYoYCell(current, prior, bold = false) {
    const yoy = formatYoY(current, prior);
    if (yoy === '--') {
      return <td className="px-2 py-2.5 text-[11px] text-right text-[#ccc]">--</td>;
    }
    return (
      <td className={`px-2 py-2.5 text-[11px] text-right ${bold ? 'font-bold' : ''} ${yoy.color}`}>
        {yoy.text}
      </td>
    );
  }

  // Line item row - clean, minimal
  function renderLineItem(category, item, rowIndex) {
    const itemYtd = lineItemYTD(category, item.key);
    const priorYtdVal = priorLineItemYTD(category, item.key);
    return (
      <tr key={item.key} className="border-b border-gray-100 hover:bg-gray-50/50">
        <td className="px-4 py-2.5 text-[13px] text-near-black whitespace-nowrap sticky left-0 z-10 bg-white">
          {item.label}
        </td>
        {MONTHS.map((m, i) => {
          const baseVal = months[m]?.[category]?.[item.key] || 0;
          return (
            <td
              key={m}
              className={`px-2 py-2.5 text-[12px] ${isEstimateCol(i) ? 'bg-bone/40' : ''}`}
            >
              <EditableCell
                value={baseVal}
                editable={editable}
                onChange={(val) => onCellChange(m, category, item.key, val)}
              />
            </td>
          );
        })}
        <td className="px-2 py-2.5 text-[12px] font-bold text-right text-near-black">
          {formatCurrency(itemYtd)}
        </td>
        <td className="px-2 py-2.5 text-[11px] text-right text-[#999]">
          {formatPercent(pctOfIncome(itemYtd))}
        </td>
        {hasPriorYear && renderYoYCell(itemYtd, priorYtdVal)}
      </tr>
    );
  }

  // ACT/EST status bar - sits above the first group header
  function renderActualEstimateBar() {
    if (actualsCutoff == null) return null;
    return (
      <tr key="act-est-bar">
        <td className="px-4 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-[#999] sticky left-0 z-10 bg-white">
          Status
        </td>
        {MONTH_HEADERS.map((_, i) => {
          const isActual = i < actualsCutoff;
          return (
            <td key={i} className="px-1 py-1.5 text-center align-middle">
              <span
                className={`inline-flex items-center justify-center w-full py-1 rounded-[2px] text-[8px] font-bold uppercase tracking-wider leading-none ${
                  isActual
                    ? 'bg-deep-teal/15 text-deep-teal'
                    : 'bg-gray-100 text-[#aaa]'
                }`}
              >
                {isActual ? 'ACT' : 'EST'}
              </span>
            </td>
          );
        })}
        <td />
        <td />
        {hasPriorYear && <td />}
      </tr>
    );
  }

  // Group header - teal background, white text
  function renderGroupHeader(label) {
    return (
      <tr key={`header-${label}`} className="bg-deep-teal">
        <td className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.1em] text-white sticky left-0 z-10 bg-deep-teal">
          {label}
        </td>
        {MONTH_HEADERS.map((mh) => (
          <td key={mh} className="px-2 py-3 text-right">
            <span className="text-[11px] font-bold text-white/80">{mh}</span>
          </td>
        ))}
        <td className="px-2 py-3 text-[11px] font-bold text-right text-white/80">YTD</td>
        <td className="px-2 py-3 text-[11px] font-bold text-right text-white/80">% Inc</td>
        {hasPriorYear && (
          <td className="px-2 py-3 text-[11px] font-bold text-right text-white/80">YoY</td>
        )}
      </tr>
    );
  }

  // Subtotal row - subtle emphasis
  function renderSubtotalRow(label, monthValues, ytdValue, pctValue, priorYtdValue) {
    const yoy = hasPriorYear ? formatYoY(ytdValue, priorYtdValue) : null;
    return (
      <tr key={`subtotal-${label}`} className="border-b border-navy/10 bg-gray-50/80">
        <td className="px-4 py-2.5 text-[13px] font-bold text-navy sticky left-0 z-10 bg-gray-50/80">
          {label}
        </td>
        {monthValues.map((val, i) => (
          <td
            key={i}
            className={`px-2 py-2.5 text-[12px] font-bold text-right ${val < 0 ? 'text-rose' : 'text-near-black'}`}
          >
            {formatCurrency(val)}
          </td>
        ))}
        <td className={`px-2 py-2.5 text-[12px] font-bold text-right ${ytdValue < 0 ? 'text-rose' : 'text-near-black'}`}>
          {formatCurrency(ytdValue)}
        </td>
        <td className="px-2 py-2.5 text-[11px] font-bold text-right text-[#888]">
          {pctValue != null ? formatPercent(pctValue) : ''}
        </td>
        {hasPriorYear && (
          <td className={`px-2 py-2.5 text-[11px] font-bold text-right ${yoy === '--' ? 'text-[#ccc]' : yoy?.color}`}>
            {yoy === '--' ? '--' : yoy?.text}
          </td>
        )}
      </tr>
    );
  }

  // Spacer between groups
  const totalCols = 1 + 12 + 2 + (hasPriorYear ? 1 : 0);
  function renderSpacer(id) {
    return (
      <tr key={`spacer-${id}`}>
        <td colSpan={totalCols} className="h-3" />
      </tr>
    );
  }

  return (
    <div>
      <table className="w-full text-[13px] border-collapse table-fixed">
        <colgroup>
          <col className="w-[220px]" />
          {MONTHS.map((m) => (
            <col key={m} className="w-[90px]" />
          ))}
          <col className="w-[100px]" />
          <col className="w-[65px]" />
          {hasPriorYear && <col className="w-[65px]" />}
        </colgroup>
        <tbody>
          {/* ACT/EST status bar */}
          {renderActualEstimateBar()}
          {/* INCOME */}
          {renderGroupHeader('Income')}
          {INCOME_ITEMS.map((item, i) => renderLineItem('income', item, i))}
          {/* Pipeline Forecast row (when enabled) */}
          {hasForecast && (
            <tr className="bg-bullogic-blue/5 border-b border-bullogic-blue/20">
              <td className="px-4 py-2 text-[13px] font-bold text-bullogic-blue whitespace-nowrap sticky left-0 z-10 bg-bullogic-blue/5">
                Pipeline Forecast
              </td>
              {MONTHS.map((m, i) => (
                <td key={m} className="px-2 py-2 text-[12px] text-right text-bullogic-blue font-bold">
                  {(forecastRevenue[i] || 0) > 0 ? formatCurrency(forecastRevenue[i]) : '--'}
                </td>
              ))}
              <td className="px-2 py-2 text-[12px] font-bold text-right text-bullogic-blue">
                {formatCurrency(forecastRevenue.reduce((s, v) => s + (v || 0), 0))}
              </td>
              <td className="px-2 py-2 text-[11px] text-right text-[#999]">
                {formatPercent(ytd.totalIncome > 0 ? forecastRevenue.reduce((s, v) => s + (v || 0), 0) / ytd.totalIncome : 0)}
              </td>
              {hasPriorYear && <td />}
            </tr>
          )}
          {/* Won deal revenue is now included directly in each income line item */}
          {/* Scenario Impact row (when enabled) */}
          {hasScenarios && (
            <tr className="bg-sky-blue/5 border-b border-sky-blue/20">
              <td className="px-4 py-2 text-[13px] font-bold text-sky-blue whitespace-nowrap sticky left-0 z-10 bg-sky-blue/5">
                Scenario Impact
              </td>
              {MONTHS.map((m, i) => {
                const net = scenarioImpact[i]?.net || 0;
                return (
                  <td key={m} className={`px-2 py-2 text-[12px] text-right font-bold ${net > 0 ? 'text-deep-teal' : net < 0 ? 'text-rose' : 'text-[#ccc]'}`}>
                    {net !== 0 ? formatCurrency(net) : '--'}
                  </td>
                );
              })}
              <td className={`px-2 py-2 text-[12px] font-bold text-right ${scenarioImpact.reduce((s, m) => s + (m.net || 0), 0) >= 0 ? 'text-deep-teal' : 'text-rose'}`}>
                {formatCurrency(scenarioImpact.reduce((s, m) => s + (m.net || 0), 0))}
              </td>
              <td className="px-2 py-2 text-[11px] text-right text-[#999]"></td>
              {hasPriorYear && <td />}
            </tr>
          )}
          {renderSubtotalRow(
            (hasForecast || hasScenarios) ? `Total Income${hasForecast ? ' + Forecast' : ''}${hasScenarios ? ' + Scenarios' : ''}` : 'Total Income',
            monthlyTotals.map((mt) => mt.totalIncome),
            ytd.totalIncome,
            null,
            priorYtd?.totalIncome
          )}

          {renderSpacer('after-income')}

          {/* COGS */}
          {renderGroupHeader('Cost of Goods Sold')}
          {COGS_ITEMS.map((item, i) => renderLineItem('cogs', item, i))}
          {renderSubtotalRow(
            'Total COGS',
            monthlyTotals.map((mt) => mt.totalCogs),
            ytd.totalCogs,
            pctOfIncome(ytd.totalCogs),
            priorYtd?.totalCogs
          )}

          {renderSpacer('after-cogs')}

          {/* GROSS PROFIT */}
          {renderSubtotalRow(
            'Gross Profit',
            monthlyTotals.map((mt) => mt.grossProfit),
            ytd.grossProfit,
            pctOfIncome(ytd.grossProfit),
            priorYtd?.grossProfit
          )}

          {renderSpacer('after-gp')}

          {/* EXPENSES */}
          {renderGroupHeader('Expenses')}
          {EXPENSE_ITEMS.map((item, i) => renderLineItem('expenses', item, i))}
          {renderSubtotalRow(
            'Total Expenses',
            monthlyTotals.map((mt) => mt.totalExpenses),
            ytd.totalExpenses,
            pctOfIncome(ytd.totalExpenses),
            priorYtd?.totalExpenses
          )}

          {renderSpacer('after-expenses')}

          {/* NET OPERATING INCOME */}
          {(() => {
            const noiYoY = hasPriorYear ? formatYoY(ytd.netOperatingIncome, priorYtd?.netOperatingIncome) : null;
            return (
              <tr className="border-t-2 border-navy/30 bg-bone">
                <td className="px-4 py-3 text-[13px] font-bold text-navy sticky left-0 z-10 bg-bone">
                  Net Operating Income
                </td>
                {monthlyTotals.map((mt, i) => (
                  <td
                    key={i}
                    className={`px-2 py-3 text-[13px] font-bold text-right ${
                      mt.netOperatingIncome < 0 ? 'text-rose' : 'text-deep-teal'
                    }`}
                  >
                    {formatCurrency(mt.netOperatingIncome)}
                  </td>
                ))}
                <td
                  className={`px-2 py-3 text-[13px] font-bold text-right ${
                    ytd.netOperatingIncome < 0 ? 'text-rose' : 'text-deep-teal'
                  }`}
                >
                  {formatCurrency(ytd.netOperatingIncome)}
                </td>
                <td className="px-2 py-3 text-[12px] font-bold text-right text-navy">
                  {formatPercent(pctOfIncome(ytd.netOperatingIncome))}
                </td>
                {hasPriorYear && (
                  <td className={`px-2 py-3 text-[12px] font-bold text-right ${noiYoY === '--' ? 'text-[#ccc]' : noiYoY?.color}`}>
                    {noiYoY === '--' ? '--' : noiYoY?.text}
                  </td>
                )}
              </tr>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
}
